import { Link } from 'react-router-dom'
import { FaArrowRight } from 'react-icons/fa'
import { getContextualCTA, formatSessionDate, OFFER_LABEL, OFFER_NOUN, type LiveOffers } from '../../../config/cta'
import { CTA, trackAttrs } from '../../../utils/track'

interface ContextualCTAProps {
  /** Article category (URL first segment / frontmatter `category`). */
  category: string
  /** What's live on Maven, from the route loader. */
  offers?: LiveOffers
}

// Topic-matched conversion block shown at the foot of every article, above the
// "Keep reading" links. It turns reader intent into the right next step: the
// paid cohort as the headline, the next free session as the low-commitment
// entry, and 1:1 mentorship on the career-shaped topics. What's on comes from
// the admin Maven table, so a finished session stops being advertised at the
// foot of 500 articles without anyone editing them.
//
// Layout: three separate cards, not one stacked panel. The cohort is the wide
// hero card; the free session and mentorship are two equal cards beneath it.
// Splitting them into distinct boxes is what makes the three offers legible at
// a glance instead of running together inside one navy block.
//
// Links are tracked (cta_maven / cta_lesson / cta_mentorship) with the offer id
// as `target`, so OpenPanel can attribute conversions to placement.
export default function ContextualCTA({ category, offers }: ContextualCTAProps) {
  if (!category) return null

  const { course, lesson, mentorship } = getContextualCTA(category, offers)
  const sessionDate = lesson ? formatSessionDate(lesson.startsAt) : ''

  // Shared shell for the two secondary cards, so the free session and the
  // mentorship read as the same kind of choice. h-full + an mt-auto footer link
  // keeps both calls to action on the same baseline even though only the session
  // card carries a date line.
  const secondaryCard =
    'group flex h-full flex-col rounded-smooth-lg bg-brand-navy p-6 border border-transparent transition-colors duration-150 hover:border-white/25'
  const secondaryLink =
    'mt-auto pt-4 inline-flex items-center gap-1.5 text-body-sm font-heading font-semibold text-brand-sky'

  return (
    <section className="max-w-4xl mx-auto px-4 mt-16">
      <div className="mb-5 flex items-center gap-2.5">
        <span className="h-0.5 w-7 bg-brand-red" aria-hidden="true" />
        <span className="text-overline text-brand-sky">Go further</span>
      </div>

      {/* ── Card 1: the paid cohort (hero) ── */}
      <a
        href={course.url}
        target="_blank"
        rel="noopener"
        {...trackAttrs(CTA.maven, 'article_cta', { target: course.id })}
        className="group block rounded-smooth-lg bg-brand-navy p-7 sm:p-9 border border-transparent transition-colors duration-150 hover:border-white/25"
      >
        <span className="inline-flex items-center rounded-smooth bg-white/10 px-2.5 py-1 text-overline text-white/75">
          {OFFER_LABEL[course.kind]}
        </span>

        <h3 className="font-heading font-bold text-h3 text-white leading-tight mt-3 max-w-2xl">
          {course.title}
        </h3>

        <p className="text-body font-body text-white/70 leading-relaxed mt-3 max-w-2xl">
          {course.blurb}
        </p>

        {course.promo ? (
          <p className="mt-4 inline-flex items-center rounded-smooth bg-brand-red/15 px-3 py-1.5 text-body-sm font-heading font-semibold text-white">
            {course.promo}
          </p>
        ) : null}

        <span className="mt-5 inline-flex items-center gap-2 rounded-smooth bg-white px-5 py-3 font-heading text-cta text-brand-navy transition-opacity duration-150 group-hover:opacity-90">
          View {OFFER_NOUN[course.kind]}
          <FaArrowRight className="text-sm" aria-hidden="true" />
        </span>
      </a>

      {/* ── Cards 2 & 3: the low-commitment alternatives ── */}
      {/* One column when no session is scheduled, so mentorship doesn't sit as a
          lone half-width card next to empty space. */}
      <div className={`mt-4 grid gap-4 ${lesson ? 'sm:grid-cols-2' : ''}`}>
        {/* Only when a session is actually scheduled — the alternative is
            inviting 500 articles' worth of readers to a lesson that's over. */}
        {lesson && (
          <a
            href={lesson.url}
            target="_blank"
            rel="noopener"
            {...trackAttrs(CTA.lesson, 'article_cta', { target: lesson.id })}
            className={secondaryCard}
          >
            <span className="text-overline text-brand-sky">Free lesson</span>
            <span className="font-heading font-semibold text-body-lg text-white mt-2">{lesson.title}</span>
            {sessionDate ? <span className="text-body-sm text-white/80 mt-1.5">{sessionDate}</span> : null}
            <span className={secondaryLink}>
              Save my seat
              <FaArrowRight
                className="text-[10px] transition-transform duration-150 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </span>
          </a>
        )}

        {/* Always shown, but the pitch matches the reader: an algorithm post
            gets interview prep, an AI post gets the architecture review. */}
        <Link
          to="/mentorship"
          {...trackAttrs(CTA.mentorship, 'article_cta', { target: mentorship.id })}
          className={secondaryCard}
        >
          <span className="text-overline text-brand-sky">{mentorship.eyebrow}</span>
          <span className="font-heading font-semibold text-body-lg text-white mt-2">{mentorship.title}</span>
          <span className={secondaryLink}>
            See how it works
            <FaArrowRight
              className="text-[10px] transition-transform duration-150 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        </Link>
      </div>
    </section>
  )
}
